import { useEffect, useMemo, useState } from 'react';
import { Button, Input, Modal, ModalBody, ModalFooter } from 'reactstrap';

type BoardMember = {
  userId: string;
  groupName?: string;
  user?: { id: string; firstName: string; lastName: string } | null;
};

type ListSetting = {
  id: string;
  name: string;
  isCompletedBucket?: boolean;
  defaultMemberUserIds: string[];
};

type BoardSettingsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  lists: ListSetting[];
  boardMembers: BoardMember[];
  onSave: (settings: { listId: string; memberUserIds: string[] }[]) => Promise<void>;
  saving: boolean;
};

const GROUP_LABEL: Record<string, string> = {
  Director: 'Director',
  EducationDirectorAdminAssistant: 'Ed Director / Admin Asst.',
  UpperManagement: 'Senior Manager',
  Manager: 'Manager',
};

const BoardSettingsModal = ({ isOpen, onClose, lists, boardMembers, onSave, saving }: BoardSettingsModalProps) => {
  const [settings, setSettings] = useState<Record<string, string[]>>({});

  useEffect(() => {
    if (!isOpen) return;
    const init: Record<string, string[]> = {};
    lists.forEach((list) => {
      init[list.id] = [...(list.defaultMemberUserIds || [])];
    });
    setSettings(init);
  }, [isOpen, lists]);

  const sortedMembers = useMemo(
    () =>
      [...boardMembers].sort((a, b) => {
        const aName = `${a.user?.firstName || ''} ${a.user?.lastName || ''}`.trim().toLowerCase();
        const bName = `${b.user?.firstName || ''} ${b.user?.lastName || ''}`.trim().toLowerCase();
        return aName.localeCompare(bName);
      }),
    [boardMembers],
  );

  const addMember = (listId: string, userId: string) => {
    setSettings((prev) => ({
      ...prev,
      [listId]: prev[listId]?.includes(userId) ? prev[listId] : [...(prev[listId] || []), userId],
    }));
  };

  const removeMember = (listId: string, userId: string) => {
    setSettings((prev) => ({
      ...prev,
      [listId]: (prev[listId] || []).filter((id) => id !== userId),
    }));
  };

  const handleSave = async () => {
    const result = Object.entries(settings).map(([listId, memberUserIds]) => ({ listId, memberUserIds }));
    await onSave(result);
  };

  const visibleLists = lists.filter((l) => !l.isCompletedBucket);

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" className="task-board__board-settings-modal">
      <div className="modal-header">
        <h3 className="modal-title">Board Auto-Assignment Settings</h3>
        <button type="button" className="close" onClick={onClose} aria-label="Close">
          <span aria-hidden="true">×</span>
        </button>
      </div>
      <ModalBody>
        <p className="text-muted mb-4">
          Configure which team members are automatically assigned when tasks are created in each list.
        </p>
        {visibleLists.map((list) => {
          const listMemberIds = settings[list.id] || [];
          const selectedMembers = sortedMembers.filter((m) => listMemberIds.includes(m.userId));
          const availableMembers = sortedMembers.filter((m) => !listMemberIds.includes(m.userId));

          return (
            <div key={list.id} className="card mb-3">
              <div className="card-body">
                <h3 className="card-title mb-3">{list.name}</h3>
                <Input
                  type="select"
                  value=""
                  onChange={(e) => {
                    if (e.target.value) addMember(list.id, e.target.value);
                  }}
                  disabled={!availableMembers.length}
                >
                  <option value="">{availableMembers.length ? 'Select a member...' : 'All members added'}</option>
                  {availableMembers.map((member) => {
                    const fullName =
                      `${member.user?.firstName || ''} ${member.user?.lastName || ''}`.trim() || 'Unknown user';
                    const profileLabel = member.groupName ? GROUP_LABEL[member.groupName] ?? member.groupName : '';
                    return (
                      <option key={member.userId} value={member.userId}>
                        {fullName}
                        {profileLabel ? ` (${profileLabel})` : ''}
                      </option>
                    );
                  })}
                </Input>
                <div className="task-board__members-list mt-2">
                  {selectedMembers.map((member) => {
                    const fullName =
                      `${member.user?.firstName || ''} ${member.user?.lastName || ''}`.trim() || 'Unknown user';
                    const initials =
                      `${member.user?.firstName?.[0] || ''}${member.user?.lastName?.[0] || ''}`.toUpperCase() || 'U';
                    const profileLabel = member.groupName ? GROUP_LABEL[member.groupName] ?? member.groupName : '';
                    return (
                      <div key={member.userId} className="task-board__member-card task-board__member-card--active">
                        <span className="task-board__member-avatar">{initials}</span>
                        <span className="task-board__member-name">
                          {fullName}
                          {profileLabel && <small className="text-muted ml-1">({profileLabel})</small>}
                        </span>
                        <Button
                          type="button"
                          size="sm"
                          color="link"
                          className="task-board__member-remove"
                          onClick={() => removeMember(list.id, member.userId)}
                        >
                          X
                        </Button>
                      </div>
                    );
                  })}
                  {!selectedMembers.length && <div className="task-board__empty-state">No members configured.</div>}
                </div>
              </div>
            </div>
          );
        })}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button color="primary" onClick={() => void handleSave()} disabled={saving}>
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default BoardSettingsModal;
